import React, { useCallback, useEffect } from "react";
import { Route, Routes, useLocation, useNavigate } from "react-router-dom";

import {
  type DisqualificationReason,
  type ProgramOffering,
  RetriageFinishedStatus,
  ScreenedOutReason,
} from "@/constants";
import { SurveyAnswer, SurveyAnswers, useAppContext } from "@/contexts";
import {
  useAnswerSurveyQuestion,
  useExperiment,
  useGoto,
  useTrackEvent,
} from "@/hooks";
import {
  qualifiesForDpp,
  qualifiesForNoomDiabetes,
  qualifiesForNoomMed,
  qualifiesForNoomWeight,
} from "@/utils/qualification";

import { QuestionSection, QuestionVisibilityState } from "./Question";
import { GLP1_WEIGHT_QUESTION_ID } from "./questionTypes/dpp-triage/Glp1WeightQuestion";

type QualificationFunction = (
  state: QuestionVisibilityState,
) => DisqualificationReason | null;

type SurveyProps = {
  baseRoute: string;
  onSurveyComplete: (surveyAnswers: SurveyAnswers) => Promise<void>;
  questionSet: QuestionSection[];
  resetOnOpen?: boolean;
};

type QualificationResult = {
  newQualifiedPrograms: ProgramOffering[];
  disqualifications: Map<ProgramOffering, DisqualificationReason>;
};

const getNewQualifiedPrograms = (
  startingPrograms: ProgramOffering[],
  state: QuestionVisibilityState,
): QualificationResult => {
  const qualificationMap: Record<ProgramOffering, QualificationFunction> = {
    DPP: qualifiesForDpp,
    DIABETES_LIFESTYLE: qualifiesForNoomDiabetes,
    MED: qualifiesForNoomMed,
    MOOD: () => null,
    NMPBA: qualifiesForNoomMed,
    WEIGHT: qualifiesForNoomWeight,
  };

  const newQualifiedPrograms: ProgramOffering[] = [];
  const disqualifications = new Map<ProgramOffering, DisqualificationReason>();

  startingPrograms.forEach((program) => {
    const disqualificationReason = qualificationMap[program](state);

    if (disqualificationReason === null) {
      newQualifiedPrograms.push(program);
    } else {
      disqualifications.set(program, disqualificationReason);
    }
  });

  return { newQualifiedPrograms, disqualifications };
};

export const Survey: React.FC<SurveyProps> = ({
  baseRoute,
  onSurveyComplete,
  questionSet,
  resetOnOpen = true,
}) => {
  const { answerSurveyQuestion, getNewSurveyAnswersState } =
    useAnswerSurveyQuestion();
  const {
    eligibilityInfo,
    enrollmentInfo,
    enrollmentType,
    hasB2cAccount,
    partnerInfo,
    qualifiedPrograms,
    retriageEligibility,
    screenedOutReason,
    selectedProgram,
    surveyAnswers,
    surveyMetadata,
    telehealthEligibleStates,
    userProfile,
    setQualifiedPrograms,
    setQuestionSet,
    setSurveyAnswers,
  } = useAppContext();
  const { getExperimentAssignments } = useExperiment();
  const { retriageFinished, screenOut } = useGoto();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const {
    trackAnonEvent,
    trackIdentifiedEvent,
    trackSensitiveEvent,
    trackSurveyAnonEvent,
    trackSurveyIdentifiedEvent,
  } = useTrackEvent();

  const experiments = getExperimentAssignments();

  const questions = questionSet.flatMap(
    (questionSection) => questionSection.questions,
  );

  const getQuestionIdFromPath = useCallback(
    () => pathname.split("/").pop() || "",
    [pathname],
  );

  const pushHistoryPreserveQuerystring = (newPathName: string) => {
    navigate({
      pathname: newPathName,
      search,
    });
  };

  const getEligiblePrograms = () => {
    if (enrollmentType === "INITIAL_ENROLLMENT") {
      return eligibilityInfo?.eligiblePrograms || [];
    }

    if (enrollmentType === "RETRIAGE") {
      return retriageEligibility?.eligibleRetriagePrograms || [];
    }

    // Recurring outcomes does not have eligible programs
    return [];
  };

  const trackUserDisqualificationEvents = useCallback(
    (
      product: ProgramOffering,
      disqualificationReason: DisqualificationReason,
    ) => {
      trackAnonEvent("b2bEnrollmentAnonUserDisqualified", {
        product,
        disqualificationReason,
      });
      trackIdentifiedEvent("b2bEnrollmentIdentifiedUserDisqualified", {
        product,
      });
      trackSensitiveEvent("b2bEnrollmentSensitiveUserDisqualified", {
        product,
        disqualificationReason,
      });
    },
    [trackAnonEvent, trackIdentifiedEvent, trackSensitiveEvent],
  );

  // TODO: Remove newState as an argument - it was added as a hack because
  // state updates occur asynchronously
  const findNextQuestion = (
    startingIndex: number,
    newState: SurveyAnswers,
    newQualifiedPrograms: ProgramOffering[],
  ) => {
    const updatedProgram =
      newState?.updatedProgram === undefined
        ? selectedProgram
        : newState?.updatedProgram;

    return questions.find(
      ({ supportedPrograms, shouldShowQuestion }, index) => {
        if (index < startingIndex) {
          return false;
        }

        if (
          (enrollmentType === "INITIAL_ENROLLMENT" ||
            enrollmentType === "RETRIAGE") &&
          supportedPrograms
        ) {
          // If the selected program is already known, then supported programs
          // must include that exact selected program
          if (updatedProgram && !supportedPrograms.includes(updatedProgram)) {
            return false;
          }

          // If the selected program is not known yet, then supported programs
          // must include one of the user's qualified programs
          if (
            !supportedPrograms.some((program) =>
              newQualifiedPrograms.includes(program),
            )
          ) {
            return false;
          }
        }

        return shouldShowQuestion({
          eligibilityInfo,
          enrollmentInfo,
          experiments,
          hasB2cAccount,
          partnerInfo,
          qualifiedPrograms: newQualifiedPrograms,
          retriageEligibility,
          screenedOutReason,
          selectedProgram: updatedProgram,
          surveyAnswers: newState,
          telehealthEligibleStates,
          userProfile,
        });
      },
    );
  };

  const goToFirstQuestion = () => {
    const firstQuestion = findNextQuestion(
      0,
      surveyAnswers,
      getEligiblePrograms(),
    );

    if (!firstQuestion) {
      return;
    }

    navigate(
      {
        pathname: `${baseRoute}/${firstQuestion.id}`,
        search,
      },
      { replace: true },
    );
  };

  const getQuestionIndexById = (questionId: string): number =>
    questions.findIndex((question) => question.id === questionId);

  /**
   *
   * @param answer to be stored at surveyAnswers[questionId]
   * @param additionalAnswers to be stored at their specified key
   */
  const onClickNext = async (
    answer: SurveyAnswer,
    additionalAnswers?: Record<string, SurveyAnswer>,
    reason?: ScreenedOutReason,
  ) => {
    const questionId = getQuestionIdFromPath();

    const newSurveyAnswers = getNewSurveyAnswersState(
      questionId,
      answer,
      additionalAnswers,
    );
    await answerSurveyQuestion(questionId, answer, additionalAnswers);

    const eligiblePrograms = getEligiblePrograms();
    let newQualifiedPrograms: ProgramOffering[] = [];

    // We have to use eligible programs instead of qualified programs because
    // if the user back navigates, we don't preserve the qualified programs
    // state at a specific question
    if (eligiblePrograms.length > 0) {
      const qualificationResult = getNewQualifiedPrograms(eligiblePrograms, {
        eligibilityInfo,
        enrollmentInfo,
        experiments,
        hasB2cAccount,
        partnerInfo,
        qualifiedPrograms,
        retriageEligibility,
        screenedOutReason,
        selectedProgram,
        surveyAnswers: newSurveyAnswers,
        telehealthEligibleStates,
        userProfile,
      });

      const { disqualifications } = qualificationResult;
      newQualifiedPrograms = qualificationResult.newQualifiedPrograms;

      // Only fire disqualification events for programs that were previously
      // qualified but are now disqualified (not for programs that were already
      // disqualified in a previous question)
      qualifiedPrograms.forEach((program) => {
        if (!newQualifiedPrograms.includes(program)) {
          const disqualificationReason = disqualifications.get(program);
          if (disqualificationReason) {
            trackUserDisqualificationEvents(program, disqualificationReason);
          }
        }
      });

      // This is unique behavior originating from the GLP-1 weight question
      // where the user should be shown a custom retriage finished screen
      if (
        enrollmentType === "RETRIAGE" &&
        questionId === GLP1_WEIGHT_QUESTION_ID &&
        newQualifiedPrograms.length === 0
      ) {
        retriageFinished(
          RetriageFinishedStatus.EXTERNAL_GLP1_NO_ELIGIBLE_PROGRAMS,
        );
        return;
      }

      if (newQualifiedPrograms.length > 0) {
        setQualifiedPrograms(newQualifiedPrograms);
      } else {
        if (enrollmentType === "INITIAL_ENROLLMENT") {
          screenOut(ScreenedOutReason.BMI_TOO_LOW);
        } else {
          retriageFinished(
            RetriageFinishedStatus.EXCLUDED_FROM_HIGHER_ACUITY_PROGRAMS,
          );
        }
        return;
      }
    }

    const questionIndex = getQuestionIndexById(questionId);
    const nextQuestion = findNextQuestion(
      questionIndex + 1,
      newSurveyAnswers,
      newQualifiedPrograms,
    );

    if (reason) {
      screenOut(reason);
      return;
    }

    if (!nextQuestion) {
      if (surveyMetadata) {
        trackSurveyAnonEvent("b2bSurveyAnonSurveyCompleted", {});
        trackSurveyIdentifiedEvent("b2bSurveyIdentifiedSurveyCompleted", {});
      }
      await onSurveyComplete(newSurveyAnswers);
    } else {
      const nextQuestionPath = pathname.replace(questionId, nextQuestion.id);
      pushHistoryPreserveQuerystring(nextQuestionPath);
    }
  };

  const onClickBack = () => {
    /**
     * This clears the answer for the question before going back so we don't
     * store answers that may not be relevant after changing previous answers.
     *
     * Examples:
     * - pregnant question will be skipped after going back and changing sex
     *   from female to male
     * - locationState question will be skipped after going back and proceeding
     *   again if a Noom Med ineligible state is selected the first time
     */
    const newSurveyAnswers = { ...surveyAnswers };
    delete newSurveyAnswers[getQuestionIdFromPath()];
    setSurveyAnswers(newSurveyAnswers);

    navigate(-1);
  };

  useEffect(() => {
    if (resetOnOpen) {
      goToFirstQuestion();

      if (surveyMetadata) {
        trackSurveyAnonEvent("b2bSurveyAnonSurveyStarted", {});
        trackSurveyIdentifiedEvent("b2bSurveyIdentifiedSurveyStarted", {});
      }
    }
  }, []);

  useEffect(() => {
    setQuestionSet(questionSet);
  }, [questionSet]);

  return (
    <Routes>
      {questions.map((question) => {
        const { id: questionId, component: Component } = question;
        return (
          <Route
            key={questionId}
            path={questionId}
            element={
              <Component
                onClickBack={onClickBack}
                onClickNext={onClickNext}
                surveyAnswers={surveyAnswers}
              />
            }
          />
        );
      })}
    </Routes>
  );
};
